<template>
  <div v-if="shouldShowPopup" class="popup-overlay" @click.self="closePopup">
    <div class="popup-container">
      <!-- Close Button -->
      <button class="close-button" @click="closePopup">
        <i class="material-icons">close</i>
      </button>

      <!-- Promotion Content -->
      <div class="promotion-content">
        <div class="promo-icon">
          <i class="material-icons">local_fire_department</i>
        </div>

        <h2 class="promo-title">{{ promotion.name }}</h2>

        <div class="promo-bonus">
          <span class="bonus-label">{{ $t('shop.promotion.bonus') }}</span>
          <span class="bonus-value">+{{ promotion.bonusPercentage }}%</span>
          <i class="material-icons">casino</i>
        </div>

        <p v-if="promotion.description" class="promo-description">
          {{ promotion.description }}
        </p>

        <!-- Time Remaining -->
        <div class="time-remaining">
          <i class="material-icons">schedule</i>
          <span>{{ $t('shop.promotion.endsIn') }}: {{ timeRemaining }}</span>
        </div>

        <!-- CTA Button -->
        <button class="btn-cta" @click="openShop">
          <i class="material-icons">shopping_cart</i>
          <span>{{ $t('shop.promotion.buyNow') }}</span>
        </button>

        <!-- Don't Show Again Checkbox -->
        <div class="dont-show-again">
          <label>
            <input
              type="checkbox"
              v-model="dontShowToday"
              @change="handleDontShowAgain"
            >
            <span>{{ $t('shop.promotion.dontShowToday') }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'PromotionPopup',

  data() {
    return {
      shouldShowPopup: false,
      dontShowToday: false,
      timeRemaining: ''
    };
  },

  computed: {
    ...mapState('monetization', ['popupPromotion']),

    promotion() {
      return this.popupPromotion;
    }
  },

  watch: {
    // Watch for promotion data to be loaded
    promotion(newVal) {
      if (newVal && !this.shouldShowPopup) {
        this.checkAndShowPopup();
      }
    }
  },

  async mounted() {
    await this.checkAndShowPopup();

    // Update time remaining every minute
    if (this.shouldShowPopup) {
      this.updateTimeRemaining();
      this.intervalId = setInterval(this.updateTimeRemaining, 60000);
    }
  },

  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },

  methods: {
    ...mapActions('monetization', ['fetchPopupPromotion']),

    async checkAndShowPopup() {
      // Check if promotion data is available
      if (!this.promotion) {
        // Try to fetch promotion if not available
        await this.fetchPopupPromotion();
        
        // If still no promotion, return
        if (!this.promotion) {
          return;
        }
      }

      // Check if already shown today
      const lastShown = localStorage.getItem('promotionPopupLastShown');
      const today = new Date().toDateString();

      if (lastShown === today) {
        console.log('Promotion popup already shown today, skipping');
        return;
      }

      // Check if user opted out for today
      const dontShowToday = localStorage.getItem('promotionPopupDontShowToday');
      if (dontShowToday === today) {
        console.log('User opted out of promotion popup for today');
        return;
      }

      // Show the popup and mark as shown
      console.log('Showing promotion popup:', this.promotion.name);
      this.shouldShowPopup = true;
      localStorage.setItem('promotionPopupLastShown', today);
    },

    updateTimeRemaining() {
      if (!this.promotion || !this.promotion.endDate) {
        return;
      }

      const now = new Date();
      const end = new Date(this.promotion.endDate);
      const diff = end - now;

      if (diff <= 0) {
        this.timeRemaining = this.$t('shop.promotion.ended');
        this.shouldShowPopup = false;
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        this.timeRemaining = `${days}d ${hours}h`;
      } else if (hours > 0) {
        this.timeRemaining = `${hours}h ${minutes}min`;
      } else {
        this.timeRemaining = `${minutes}min`;
      }
    },

    handleDontShowAgain() {
      if (this.dontShowToday) {
        const today = new Date().toDateString();
        localStorage.setItem('promotionPopupDontShowToday', today);
      } else {
        localStorage.removeItem('promotionPopupDontShowToday');
      }
    },

    openShop() {
      this.closePopup();
      this.$emit('open-shop');
    },

    closePopup() {
      this.shouldShowPopup = false;
    }
  }
};
</script>

<style scoped lang="scss">
$primary-red: #ff4646;
$primary-blue: #3498db;
$dark-bg: #1a1a1a;
$darker: #0d0d0d;
$gold: #ffd700;

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20000;
  padding: 20px;
  animation: fadeIn 0.4s ease;
}

.popup-container {
  background: linear-gradient(135deg, $darker 0%, $dark-bg 50%, $darker 100%);
  border: 3px solid $primary-red;
  border-radius: 16px;
  box-shadow: 0 0 60px rgba($primary-red, 0.8), inset 0 0 40px rgba($primary-red, 0.1);
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;
  animation: scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba($primary-red, 0.1),
      transparent
    );
    animation: shine 3s infinite;
  }
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba($primary-red, 0.3);
  border: 2px solid $primary-red;
  color: $primary-red;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: $primary-red;
    color: white;
    transform: rotate(90deg) scale(1.1);
  }

  .material-icons {
    font-size: 24px;
  }
}

.banner-image {
  width: 100%;
  height: 250px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(to top, $darker, transparent);
  }
}

.promotion-content {
  padding: 40px 30px 30px;
  text-align: center;

  .promo-icon {
    margin-bottom: 20px;
    animation: pulse 2s infinite;

    .material-icons {
      font-size: 80px;
      color: $primary-red;
      filter: drop-shadow(0 0 20px rgba($primary-red, 0.8));
    }
  }

  .promo-title {
    font-family: 'Cinzel Decorative', serif;
    font-size: 2.2rem;
    color: $primary-red;
    text-shadow: 0 0 30px rgba($primary-red, 0.6);
    margin-bottom: 25px;
    animation: glow 2s ease-in-out infinite;
  }

  .promo-bonus {
    background: rgba($primary-red, 0.2);
    border: 2px solid $primary-red;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;

    .bonus-label {
      color: white;
      font-size: 1.1rem;
    }

    .bonus-value {
      font-size: 2.5rem;
      font-weight: bold;
      color: $primary-red;
      text-shadow: 0 0 15px rgba($primary-red, 0.8);
    }

    .material-icons {
      color: $gold;
      font-size: 32px;
    }
  }

  .promo-description {
    color: rgba(white, 0.8);
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 25px;
  }

  .time-remaining {
    background: rgba($primary-blue, 0.1);
    border: 1px solid $primary-blue;
    border-radius: 8px;
    padding: 12px 20px;
    margin-bottom: 25px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: $primary-blue;
    font-weight: bold;

    .material-icons {
      font-size: 20px;
    }
  }

  .btn-cta {
    width: 100%;
    padding: 18px;
    background: linear-gradient(135deg, $primary-red, #c0392b);
    border: none;
    border-radius: 10px;
    color: white;
    font-size: 1.3rem;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    transition: all 0.3s ease;
    margin-bottom: 20px;
    box-shadow: 0 0 30px rgba($primary-red, 0.5);

    &:hover {
      background: linear-gradient(135deg, #ff1a1a, $primary-red);
      box-shadow: 0 0 40px rgba($primary-red, 0.8);
      transform: translateY(-3px) scale(1.02);
    }

    .material-icons {
      font-size: 28px;
    }
  }

  .dont-show-again {
    margin-top: 15px;

    label {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      color: rgba(white, 0.6);
      font-size: 0.95rem;
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: white;
      }

      input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
      }
    }
  }
}

// Animations
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes glow {
  0%, 100% {
    text-shadow: 0 0 30px rgba($primary-red, 0.6);
  }
  50% {
    text-shadow: 0 0 50px rgba($primary-red, 1);
  }
}

@keyframes shine {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

// Responsive
@media (max-width: 768px) {
  .popup-container {
    margin: 10px;
  }

  .banner-image {
    height: 180px;
  }

  .promotion-content {
    padding: 30px 20px 20px;

    .promo-icon .material-icons {
      font-size: 60px;
    }

    .promo-title {
      font-size: 1.6rem;
    }

    .promo-bonus .bonus-value {
      font-size: 2rem;
    }

    .btn-cta {
      font-size: 1.1rem;
      padding: 15px;
    }
  }
}
</style>
